import type { Instance } from "@/context/auth/AuthContext";
import type { NavigationAction } from "@/lib/navigation-action";

/**
 * Destinations de l'accueil (liens des cartes et raccourcis). Elles dépendent
 * de l'instance : mode du CMS (simple / avancé), e-shop, modules activés.
 */
export type DestinationContext = {
  hasCms: boolean;
  /** CMS en mode avancé seul : les pages du CMS simple redirigent. */
  advancedCms: boolean;
  /** CMS avancé ouvert (mode avancé ou mixte). */
  advancedEnabled: boolean;
  haveEshop: boolean;
  hideCr: boolean;
  photoModule: boolean;
};

export type RouteDestination = {
  kind: "route";
  to: string;
  /** Modale à ouvrir à l'arrivée sur la page (cf. navigation-action). */
  action?: NavigationAction;
  /** State de navigation attendu par la page cible. */
  state?: Record<string, unknown>;
};

/**
 * `no-cms` : page du site internet alors que l'instance n'a pas de site — on
 * ouvre la modale d'information. `null` : indisponible, le lien est masqué.
 */
export type Destination = RouteDestination | { kind: "no-cms" } | null;

export function destinationContext(
  instance: Instance | null | undefined,
): DestinationContext {
  return {
    hasCms: Boolean(instance?.CMS?.id),
    advancedCms: instance?.CMS?.cmsAccessMode === "ADVANCED",
    advancedEnabled: instance?.CMS?.advancedCmsEnabled === true,
    haveEshop: instance?.haveEshop === true,
    hideCr: instance?.hideCr === true,
    photoModule: instance?.isModulePhotoEnabled === true,
  };
}

export const route = (
  to: string,
  options: Omit<RouteDestination, "kind" | "to"> = {},
): RouteDestination => ({ kind: "route", to, ...options });

/** Page du site internet : chemin selon le mode du CMS, ou modale si pas de site. */
const cms =
  (simple: string, advanced: string = simple) =>
  (ctx: DestinationContext): Destination =>
    !ctx.hasCms
      ? { kind: "no-cms" }
      : route(ctx.advancedCms ? advanced : simple);

export const DESTINATIONS = {
  artworks: () => route("/dashboard/artwork"),
  artworkNew: () => route("/dashboard/artwork", { action: "new" }),
  artworkSearch: () =>
    route("/dashboard/artwork", { action: "advanced-search" }),
  // L'export choisit sa table d'après la page d'origine.
  exports: () =>
    route("/export", { state: { prevLocation: "/dashboard/artwork" } }),
  importArtworks: () => route("/import?table=artwork"),
  // Contacts = collectionneurs tant qu'il n'existe pas de module Contacts.
  collectors: () => route("/dashboard/collector"),
  collectorNew: () => route("/dashboard/collector", { action: "new" }),
  collectorSearch: () =>
    route("/dashboard/collector", { action: "advanced-search" }),
  exhibitionNew: () => route("/dashboard/exhibition", { action: "new" }),
  authorNew: () => route("/dashboard/author", { action: "new" }),
  bibliographyNew: () => route("/dashboard/bibliography", { action: "new" }),
  auctionNew: () => route("/dashboard/auction", { action: "new" }),
  documentNew: () => route("/dashboard/document", { action: "new" }),
  multipleprintNew: () => route("/dashboard/multipleprint", { action: "new" }),
  writingNew: () => route("/dashboard/writing", { action: "new" }),
  audiovisualNew: () => route("/dashboard/audiovisual", { action: "new" }),
  photographyNew: (ctx: DestinationContext) =>
    ctx.photoModule ? route("/dashboard/photography", { action: "new" }) : null,
  notes: () => route("/dashboard/notes"),
  trash: () => route("/trash"),
  settings: () => route("/settings"),
  website: cms("/cms", "/cms/advanced/accueil"),
  selections: cms("/cms/selection", "/cms/advanced/selection-rubrique"),
  news: cms("/cms/news", "/cms/advanced/news"),
  catalogueRaisonne: (ctx: DestinationContext) =>
    ctx.hideCr
      ? null
      : cms("/cms/catalogue-raisonne", "/cms/advanced/catalogue")(ctx),
  authentications: cms("/cms/artwork-authentication"),
  eshop: (ctx: DestinationContext) => (ctx.haveEshop ? route("/e-shop") : null),
} satisfies Record<string, (ctx: DestinationContext) => Destination>;

export type DestinationId = keyof typeof DESTINATIONS;

export function resolveDestination(
  id: DestinationId,
  ctx: DestinationContext,
): Destination {
  return DESTINATIONS[id](ctx);
}
